import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useWorkspace } from '../../context/WorkspaceContext';
import { CloudBackend } from './cloud';
import { LocalBackend } from './local';
import type { Backend } from './types';

const BackendCtx = createContext<Backend | null>(null);

/**
 * Provides the active data backend: signed in → CloudBackend bound to the
 * ACTIVE WORKSPACE owner (own move, or a move shared with the user), otherwise
 * → on-device LocalBackend (guest mode). Always non-null while auth is
 * resolved, so the app works with or without an account.
 */
export function BackendProvider({ children }: { children: ReactNode }) {
  const { user, isLoading } = useAuth();
  const { activeOwnerUid } = useWorkspace();
  const backend = useMemo<Backend | null>(() => {
    if (isLoading) return null; // wait until we know if there's a user
    return user ? new CloudBackend(activeOwnerUid ?? user.uid) : new LocalBackend();
  }, [user, isLoading, activeOwnerUid]);
  return <BackendCtx.Provider value={backend}>{children}</BackendCtx.Provider>;
}

/** The active backend. Throws if used outside a signed-in (or guest) context. */
export function useBackend(): Backend {
  const b = useContext(BackendCtx);
  if (!b) throw new Error('No active backend');
  return b;
}

/** Non-throwing variant for places that render before a backend exists. */
export function useOptionalBackend(): Backend | null {
  return useContext(BackendCtx);
}
